import { validationHelper } from 'libs/helper/validationHelper';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, InputField, Row, useModelStateContext } from 'saladsoft-primitive';
import { EmailLoginDto, TokenDto } from 'services/data-contracts';
import ServiceHelper from 'stores/ServiceHelper';
import styled from 'styled-components';
import { format } from 'react-string-format';

const LoginPage = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { stateModel, setState } = useModelStateContext<EmailLoginDto>();

  const [isValid, setIsValid] = useState(false);
  const [errors, setErrors] = useState({
    email: '',
    password: '',
  });
  const [showErrors, setShowErrors] = useState({
    email: false,
    password: false,
  });

  const validateField = (field: 'email' | 'password', value: string) => {
    if (!value) return ''; // 값이 없으면 에러 메시지 출력하지 않음

    if (field === 'email') {
      if (!validationHelper.email(value)) {
        return t('emailHepler');
      }
    } else if (field === 'password') {
      if (!validationHelper.password(value)) {
        return t('passwordHelper');
      }
    }
    return '';
  };

  const handleChange = (field: 'username' | 'password', value: string) => {
    setState(field, value);
    // 에러 상태만 업데이트하고, showErrors는 변경하지 않음
    setErrors((prev) => ({
      ...prev,
      [field === 'username' ? 'email' : 'password']: validateField(
        field === 'username' ? 'email' : 'password',
        value,
      ),
    }));

    // 값이 입력되면 에러 메시지 숨기기
    setShowErrors((prev) => ({
      ...prev,
      [field === 'username' ? 'email' : 'password']: false,
    }));
  };

  const handleBlur = (field: 'email' | 'password') => {
    const value = field === 'email' ? stateModel?.username : stateModel?.password;
    if (!value) return;

    // blur 시에만 에러 메시지 표시 설정
    setShowErrors((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const handleEmailLogin = async () => {
    if (stateModel) {
      const response = await ServiceHelper.authService?.loginWithEmail(stateModel);
      if (response?.status === 200) {
        const tokens = response.data as TokenDto;
        if (tokens.accessToken && tokens.refreshToken) {
          await localStorage.setItem('accessToken', tokens.accessToken);
          await localStorage.setItem('refreshToken1', tokens.refreshToken);

          navigate('/title-cleansing');
        } else {
          toast.warning(t('failToLogin'));
        }
      } else {
        if (response?.status === 400) {
          const newResponse = response as any;
          if (newResponse.response.data.errorCode === 'AUTH.EMAIL_NOT_VERIFIED') {
            toast.warning(t('emailNotVerified'));
            return;
          } else {
            toast.warning(t('failToLogin'));
            return;
          }
        }
        toast.warning(t('failToLogin'));
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValid) {
      handleEmailLogin();
    }
  };

  useEffect(() => {
    const userName = validationHelper.email(stateModel?.username);
    const password = validationHelper.password(stateModel?.password);
    setIsValid(userName && password);
  }, [stateModel]);

  useEffect(() => {
    // 2025-03-25 기존의 모든 storage를 제거하는 것에서 로그인 토큰 관련 storage만 제거
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken1');
    //localStorage.clear();
  }, []);

  return (
    <Container>
      <LoginBg
        style={{ flex: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
      >
        <div style={{ margin: 'auto' }}>
          <div style={{ fontSize: 20, fontWeight: 'bold', color: 'white' }}>KSE</div>
          <div>
            <img src="/images/customsai.svg" style={{ width: 200 }} />
          </div>

          <div style={{ fontSize: 12, fontWeight: '400', color: 'white', opacity: 0.5 }}>
            {/* {packageJson.version} */}
          </div>
          <div style={{ marginTop: 40, color: 'white', opacity: 0.5 }}>
            Leading logistics systems with
            <br /> AI technology.
          </div>
        </div>
      </LoginBg>
      <div
        style={{
          flex: 0.5,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          flexDirection: 'column',
        }}
      >
        <LoginText>Log in</LoginText>
        <Row flexDirection="column" gap="30px" width="100%" maxWidth={500}>
          <InputWrapper>
            <InputField
              label="email"
              onChange={(e) => handleChange('username', e)}
              onBlur={() => handleBlur('email')}
              onKeyDown={handleKeyPress}
              helperText={showErrors.email && stateModel?.username ? errors.email : ''}
              error={!!errors.email && !!stateModel?.username}
            />
          </InputWrapper>
          <InputWrapper>
            <InputField
              label="password"
              type="password"
              onChange={(e) => handleChange('password', e)}
              onBlur={() => handleBlur('password')}
              onKeyDown={handleKeyPress}
              helperText={showErrors.password && stateModel?.password ? errors.password : ''}
              error={!!errors.password && !!stateModel?.password}
            />
          </InputWrapper>
          <div
            onClick={() => {
              navigate('/signup');
            }}
            style={{ textAlign: 'left', width: '100%', fontSize: 14, cursor: 'pointer' }}
          >
            {/* {t('signupInfo')} */}
          </div>
          <div style={{ marginTop: '10px', width: '100%' }}>
            <Button
              isValid={isValid}
              onClick={() => {
                handleEmailLogin();
              }}
            >
              {t('login')}
            </Button>
          </div>
        </Row>
        <div
          style={{
            width: '100%',
            maxWidth: 500,
            marginTop: 20,
            fontSize: 12,
            fontWeight: '400',
            textAlign: 'left',
          }}
        >
          {format(t('signupPage'), <Link to="/signup">{t('signup')}</Link>)}
        </div>
      </div>
    </Container>
  );
};

const Container = styled.div`
  justify-content: space-between;
  display: flex;
  width: 100vw;
  height: 100vh;
  /* max-width: 1800px; */
  margin: auto;
  justify-content: center;
  align-items: center;
`;

const InputWrapper = styled.div`
  width: 100%;
  height: 80px;
`;

const LoginBg = styled.div`
  background-image: url('/images/loginbg.jpg');
  background-size: cover;
  background-position: center;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  animation: slideBackground 20s ease-out infinite alternate;

  @keyframes slideBackground {
    from {
      background-position: -240px center;
    }
    to {
      background-position: 0px center;
    }
  }
`;

const LoginText = styled.div`
  text-align: left;
  width: 500px;
  font-size: 30px;
  font-weight: 600;
  margin-bottom: 40px;
`;

export default LoginPage;
